import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import type {
  CssFontSource,
  CustomFontSource,
  StripeElements,
} from '@stripe/stripe-js';

import { useSafeOptions } from '../../hooks/useSafeOptions';
import { useStripe } from './StripeContext';
import { StripeElementsContext } from './StripeElementsContext';

export const StripeFonts: CustomFontSource[] = [
  {
    family: 'neue-haas-grotesk-display',
    src: `url('https://static.thefarmersdog.com/fonts/NeueHaasGrotDispay/NeueHaasGrotDisp-55Roman-Web.woff')`,
    display: 'auto',
    style: 'normal',
    weight: '500',
  },
];

export interface StripeElementsProviderProps {
  /** An array of custom fonts, which elements created can use */
  fonts?: Array<CssFontSource | CustomFontSource>;
  children: ReactNode | ReactNode[];
}

/** Create and provides a stripe elements object to subscribers. */
export function StripeElementsProvider({
  children,
  fonts = StripeFonts,
  ...props
}: StripeElementsProviderProps) {
  const [stripeElements, setStripeElements] = useState<StripeElements>();
  const safeOptions = useSafeOptions({ fonts, ...props });
  const { stripe } = useStripe();

  useEffect(() => {
    if (!stripe) {
      // Stripe is a required dependency for this component (should it throw?)
      return;
    }

    setStripeElements(stripe.elements(safeOptions));
  }, [stripe, safeOptions]);

  const contextValue = useMemo(() => stripeElements, [stripeElements]);

  return (
    <StripeElementsContext.Provider value={contextValue}>
      {children}
    </StripeElementsContext.Provider>
  );
}
